import { authAppUrl } from "@/config/authAppUrl";
import AuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AuthService";
import { useLogout } from "@/hooks/queries/auth/useAuthQueries";
import { useCallback, useEffect } from "react";

/**
 * `/new-logout` — clears BOTH sessions while the legacy and standalone auth
 * stacks coexist:
 *   1. the legacy lazo-web session (same contract as `Logout`: revoke the
 *      token server-side / expire the HttpOnly `lazo_auth_token` cookie,
 *      then wipe client storage) — this half must run on a lazo-web origin;
 *   2. the standalone auth-api session, by handing off to auth/app's
 *      `/logout`, which POSTs `/v1/sign-out` from an origin already in
 *      auth-api's CORS allow-list and clears the `lazo_session` cookie.
 * Like `Logout`, the handoff happens even when the backend call fails — the
 * user must never be left looking logged in.
 */
export interface NewLogoutProps {
  /** Test seam. When provided, used in place of `window.location.replace`. */
  navigate?: (url: string) => void;
  /** Test seam. When provided, used in place of `window.location.origin`. */
  currentOrigin?: string;
}

export const NewLogout = ({ navigate, currentOrigin }: NewLogoutProps) => {
  const { mutateAsync } = useLogout();

  const logout = useCallback(async () => {
    try {
      await mutateAsync();
    } catch {
      // Even if revocation fails, still clear the client and hand off so the
      // user is never left looking logged in.
    } finally {
      // logOut() first so its LOGOUT observer event fires while keys still exist.
      AuthService.logOut();
      localStorage.clear();
      sessionStorage.clear();
      const origin = currentOrigin ?? window.location.origin;
      const target = `${authAppUrl()}/logout?returnTo=${encodeURIComponent(origin)}`;
      if (navigate) {
        navigate(target);
      } else {
        window.location.replace(target);
      }
    }
  }, [mutateAsync, navigate, currentOrigin]);

  useEffect(() => {
    logout();
  }, [logout]);

  return <>Logout</>;
};
